import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import moment from 'moment';
import { formatTaskContext, priorityTone } from '../pages/kanbanBoard/ganttHelpers';

const formatDue = (value) => {
    const m = moment(value);
    if (!m.isValid()) return null;
    return `due ${m.format('D MMM')}`;
};

const hoursLabel = (task) => {
    const h = Number(task.allocated_hours ?? task.estimated_hours);
    if (!Number.isFinite(h) || h <= 0) return null;
    return `${h}h`;
};

export default function TimelineRunOrder({
    tasks = [],
    onDragEnd,
    droppableId = 'timeline-run-order',
    highlightOverlapIds = null,
    triggerCardId = null,
    extraMeta = null,
}) {
    const overlap =
        highlightOverlapIds instanceof Set ? highlightOverlapIds : new Set(highlightOverlapIds || []);

    return (
        <aside className="tl-runorder">
            <div className="tl-runorder-head">
                Run order
                <span className="tl-runorder-count">{tasks.length}</span>
            </div>
            <p className="tl-runorder-hint">
                Higher items run first. Reorder to change what you pick up next; it does not move due
                dates.
            </p>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId={droppableId}>
                    {(droppableProvided) => (
                        <ul
                            className="tl-runorder-list"
                            ref={droppableProvided.innerRef}
                            {...droppableProvided.droppableProps}
                        >
                            {tasks.map((task, index) => {
                                const tone = priorityTone(task.priority);
                                const hours = hoursLabel(task);
                                const due = formatDue(task.deadline_at);
                                const isOverlap = overlap.has(Number(task.id));
                                const isTrigger = triggerCardId && Number(task.id) === Number(triggerCardId);
                                return (
                                    <Draggable
                                        key={String(task.id)}
                                        draggableId={String(task.id)}
                                        index={index}
                                    >
                                        {(provided, snapshot) => (
                                            <li
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                className={`tl-runorder-card${
                                                    snapshot.isDragging ? ' is-dragging' : ''
                                                }${isOverlap ? ' is-overlap' : ''}${
                                                    isTrigger ? ' is-trigger' : ''
                                                }${task.schedule_move?.is_unseen ? ' has-move' : ''}`}
                                            >
                                                <span
                                                    className="tl-runorder-handle"
                                                    {...provided.dragHandleProps}
                                                    title="Drag to reorder"
                                                >
                                                    ⋮⋮
                                                </span>
                                                <span className="tl-runorder-num">{index + 1}</span>
                                                <div className="tl-runorder-body">
                                                    <div className="tl-runorder-title">
                                                        <strong>{task.title}</strong>
                                                        <span className="tl-runorder-tags">
                                                            {formatTaskContext(task) || ''}
                                                        </span>
                                                    </div>
                                                    <div className="tl-runorder-meta">
                                                        {task.priority && (
                                                            <span className={`tl-pri tl-pri-${tone}`}>
                                                                {task.priority}
                                                            </span>
                                                        )}
                                                        {hours && <span>{hours}</span>}
                                                        {due && <span>{due}</span>}
                                                        {extraMeta?.(task)}
                                                    </div>
                                                </div>
                                            </li>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {droppableProvided.placeholder}
                        </ul>
                    )}
                </Droppable>
            </DragDropContext>
        </aside>
    );
}
